import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useState } from "react";
import localStorageConstants from "src/app/constants/localStorage.constants";
import { CalendarSlot } from "src/app/models/screens/requestManagement.model";
import {
  getAcceptedRequestsForDate,
  getParentChildren,
  searchUsers,
} from "src/app/utils/backendEndpointCalls.util";

interface ParentChild {
  _id: string;
  firstName: string;
}

interface AdminUser {
  id: string;
  firstName: string;
  lastName: string;
}

interface SelectorUser {
  id: string;
  label: string;
}

const MyCalendarViewModel = () => {
  const [ownCalendarDate, setOwnCalendarDate] = useState<Dayjs | null>(dayjs());
  const [
    acceptedRequestsForOwnSelectedDate,
    setAcceptedRequestsForOwnSelectedDate,
  ] = useState<CalendarSlot[]>([]);
  const [isLoadingOwnAcceptedRequests, setIsLoadingOwnAcceptedRequests] =
    useState<boolean>(false);
  const [isSnackbarError, setIsSnackbarError] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [isDisplayingSnackbar, setIsDisplayingSnackbar] =
    useState<boolean>(false);

  const [role, setRole] = useState<string>("");
  const [children, setChildren] = useState<ParentChild[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [selectorUsers, setSelectorUsers] = useState<SelectorUser[]>([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState<boolean>(false);
  const [selectedCalendarOwnerId, setSelectedCalendarOwnerId] =
    useState<string>("you");

  useEffect(() => {
    const storedRole = localStorage.getItem(localStorageConstants.ROLE) ?? "";
    setRole(storedRole);
  }, []);

  const loadParentChildren = useCallback(async () => {
    setIsLoadingChildren(true);
    const response = await getParentChildren();
    setIsLoadingChildren(false);

    if (!response.success) {
      showError(response.error);
      return;
    }

    const fetchedChildren = response.children ?? [];
    setChildren(fetchedChildren);
    setAdminUsers([]);
    setSelectorUsers(
      fetchedChildren.map((child: any) => ({
        id: child._id,
        label: child.firstName,
      })),
    );
  }, []);

  const loadAdminUsers = useCallback(async () => {
    setIsLoadingChildren(true);
    const response = await searchUsers("");
    setIsLoadingChildren(false);

    if (!response.success) {
      showError(response.error);
      return;
    }

    const fetchedUsers = response.users ?? [];
    setAdminUsers(fetchedUsers);
    setChildren([]);
    setSelectorUsers(
      fetchedUsers.map((user: any) => ({
        id: user.id,
        label: `${user.firstName} ${user.lastName}`,
      })),
    );
  }, []);

  const loadOwnAcceptedRequestsForDate = useCallback(
    async (isoDate: string, childId?: string) => {
      setIsLoadingOwnAcceptedRequests(true);
      const response =
        role === "Admin" && childId
          ? await getAcceptedRequestsForDate(isoDate, undefined, childId)
          : await getAcceptedRequestsForDate(isoDate, childId);
      setIsLoadingOwnAcceptedRequests(false);
      if (!response.success) {
        showError(response.error);
        return;
      }
      const sortedRequests = [...(response.requests ?? [])].sort(
        (a, b) => a.from - b.from,
      );
      setAcceptedRequestsForOwnSelectedDate(sortedRequests);
    },
    [role],
  );

  useEffect(() => {
    if (role === "Parent") {
      loadParentChildren();
      return;
    }

    if (role === "Admin") {
      loadAdminUsers();
      return;
    }

    setChildren([]);
    setAdminUsers([]);
    setSelectorUsers([]);
  }, [loadAdminUsers, loadParentChildren, role]);

  useEffect(() => {
    if (!ownCalendarDate) {
      setAcceptedRequestsForOwnSelectedDate([]);
      return;
    }

    loadOwnAcceptedRequestsForDate(
      ownCalendarDate.format("YYYY-MM-DD"),
      selectedCalendarOwnerId === "you" ? undefined : selectedCalendarOwnerId,
    );
  }, [
    loadOwnAcceptedRequestsForDate,
    ownCalendarDate,
    selectedCalendarOwnerId,
  ]);

  const onOwnCalendarDateChanged = (date: Dayjs | null) => {
    setOwnCalendarDate(date);
  };

  const onSelectedCalendarOwnerChanged = (ownerId: string) => {
    setSelectedCalendarOwnerId(ownerId);
  };

  const minutesToLabel = (minutes: number): string => {
    const hours: number = Math.floor(minutes / 60);
    const mins: number = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  };

  const showError = (message: string) => {
    setIsSnackbarError(true);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  return {
    ownCalendarDate,
    onOwnCalendarDateChanged,
    isLoadingOwnAcceptedRequests,
    acceptedRequestsForOwnSelectedDate,
    minutesToLabel,
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    role,
    children,
    adminUsers,
    selectorUsers,
    isLoadingChildren,
    selectedCalendarOwnerId,
    onSelectedCalendarOwnerChanged,
  };
};

export default MyCalendarViewModel;
